import { Box, FormControl, FormGroup, FormLabel, Typography } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { FC } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useEvolveUser from 'src/hooks/useEvolveUser';
import { getPatientName } from 'src/shared/utils';
import { BRANDING_CONFIG, getAllPractitionerCredentials, getQuestionnaireResponseByLinkId } from 'utils';
import {
  ExcuseFormValues,
  getDefaultExcuseFormValues,
  mapExcuseTypeToFields,
  mapValuesToExcuse,
} from '../../../../telemed/utils/school-work-excuse.helper';
import { useAppointmentData, useChartData, useSaveChartData } from '../../../stores/appointment/appointment.store';
import { ControlledExcuseCheckbox } from './ControlledExcuseCheckbox';
import { ControlledExcuseDatePicker } from './ControlledExcuseDatePicker';
import { ControlledExcuseTextField } from './ControlledExcuseTextField';
import { GenerateExcuseDialogContainer } from './GenerateExcuseDialogContainer';

type GenerateExcuseDialogExtendedProps = {
  open: boolean;
  onClose: () => void;
  type: keyof typeof mapExcuseTypeToFields;
  generate: ReturnType<typeof useSaveChartData>['mutate'];
};

export const GenerateExcuseDialog: FC<GenerateExcuseDialogExtendedProps> = (props) => {
  const { t } = useTranslation();
  const { open, onClose, type, generate } = props;
  const fields = mapExcuseTypeToFields[type];
  const isSchool = ['schoolTemplate', 'schoolFree'].includes(type);
  const isTemplate = ['schoolTemplate', 'workTemplate'].includes(type);
  const { patient, questionnaireResponse } = useAppointmentData();
  const { setPartialChartData, chartData } = useChartData();
  const user = useEvolveUser();

  const responsibleParty = {
    firstName: getQuestionnaireResponseByLinkId('responsible-party-first-name', questionnaireResponse)?.answer?.[0]
      ?.valueString,
    lastName: getQuestionnaireResponseByLinkId('responsible-party-last-name', questionnaireResponse)?.answer?.[0]
      ?.valueString,
    relationship: getQuestionnaireResponseByLinkId('responsible-party-relationship', questionnaireResponse)?.answer?.[0]
      ?.valueString,
  };
  const fullParentName =
    responsibleParty.firstName &&
    responsibleParty.lastName &&
    ['Parent', 'Legal Guardian'].includes(responsibleParty.relationship ?? '')
      ? `${responsibleParty.firstName} ${responsibleParty.lastName}`
      : '';
  const methods = useForm<ExcuseFormValues>({
    defaultValues: getDefaultExcuseFormValues({
      isSchool,
      isTemplate,
      patientName: getPatientName(patient?.name).firstLastName,
      parentName: fullParentName,
      providerName: user?.userName,
      suffix: user?.profileResource?.name?.[0]?.suffix?.join(' '),
      phoneNumber: BRANDING_CONFIG.email.supportPhoneNumber,
      t,
    }),
  });
  const { handleSubmit, getValues, setValue } = methods;

  const onSubmit = (values: ExcuseFormValues): void => {
    const excuse = mapValuesToExcuse(values, {
      type,
      isSchool,
      isTemplate,
      patientName: getPatientName(patient?.name).firstLastName,
      providerName: user?.userName,
      suffix: user?.profileResource ? getAllPractitionerCredentials(user?.profileResource).join(' ') : undefined,
      t,
    });
    generate(
      { newSchoolWorkNote: excuse },
      {
        onSuccess: (data) => {
          const savedExcuses = data?.chartData?.schoolWorkNotes;
          if (savedExcuses) {
            setPartialChartData({
              schoolWorkNotes: [...(chartData?.schoolWorkNotes || []), ...savedExcuses],
            });
          }
        },
        onError: () => {
          enqueueSnackbar(t('telemed.plan.excuse.fields.errorGenerate'), {
            variant: 'error',
          });
        },
      }
    );
    onClose();
  };

  return (
    <GenerateExcuseDialogContainer
      open={open}
      onSubmit={handleSubmit(onSubmit)}
      onClose={onClose}
      label={isSchool ? t('telemed.plan.excuse.schoolExcuse') : t('telemed.plan.excuse.workExcuse')}
    >
      <FormProvider {...methods}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {fields.includes('headerNote') && (
            <ControlledExcuseTextField
              name="headerNote"
              label={t('telemed.plan.excuse.fields.note')}
              fullWidth
              multiline
              required
            />
          )}

          {isTemplate && (
            <FormControl>
              <FormLabel>
                <Typography variant="subtitle2">{t('telemed.plan.excuse.fields.selectWhateverApplies')}</Typography>
              </FormLabel>
              <FormGroup>
                {fields.includes('workFields') && (
                  <>
                    <ControlledExcuseCheckbox
                      name="wereWithThePatientAtTheTimeOfTheVisit"
                      label={t(`telemed.plan.excuse.fields.wereWithThePatientAtTheTimeOfTheVisit`)}
                    />
                    <ControlledExcuseCheckbox
                      name="areNeededAtHomeToCareForChildDuringThisIllness"
                      label={t(`telemed.plan.excuse.fields.areNeededAtHomeToCareForChildDuringThisIllness`)}
                    />
                    <Box sx={{ display: 'flex' }}>
                      <ControlledExcuseCheckbox
                        name="workExcusedFromWorkFromTo"
                        label={t(`telemed.plan.excuse.fields.workExcusedFromWorkFromTo`)}
                      />

                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <ControlledExcuseDatePicker
                          name="workExcusedFromWorkFromDate"
                          validate={(value) => {
                            if (getValues('workExcusedFromWorkFromTo') && !value) {
                              return t('telemed.plan.excuse.fields.required');
                            }
                            return;
                          }}
                        />
                        <ControlledExcuseDatePicker
                          name="workExcusedFromWorkToDate"
                          validate={(value) => {
                            if (getValues('workExcusedFromWorkFromTo') && !value) {
                              return t('telemed.plan.excuse.fields.required');
                            }
                            return;
                          }}
                        />
                      </Box>
                    </Box>

                    <Box>
                      <ControlledExcuseCheckbox
                        name="workExcusedFromWorkOn"
                        label={t(`telemed.plan.excuse.fields.workExcusedFromWorkOn`)}
                      />

                      <ControlledExcuseDatePicker
                        name="workExcusedFromWorkOnDate"
                        validate={(value) => {
                          if (getValues('workExcusedFromWorkOn') && !value) {
                            return t('telemed.plan.excuse.fields.required');
                          }
                          return;
                        }}
                      />
                    </Box>
                  </>
                )}

                {fields.includes('schoolFields') && (
                  <>
                    <Box sx={{ display: 'flex' }}>
                      <ControlledExcuseCheckbox
                        name="excusedFromSchoolFromTo"
                        label={t(`telemed.plan.excuse.fields.excusedFromSchoolFromTo`)}
                      />
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <ControlledExcuseDatePicker
                          name="excusedFromSchoolFromDate"
                          validate={(value) => {
                            if (getValues('excusedFromSchoolFromTo') && !value) {
                              return t('telemed.plan.excuse.fields.required');
                            }
                            return;
                          }}
                        />
                        <ControlledExcuseDatePicker
                          name="excusedFromSchoolToDate"
                          validate={(value) => {
                            if (getValues('excusedFromSchoolFromTo') && !value) {
                              return t('telemed.plan.excuse.fields.required');
                            }
                            return;
                          }}
                        />
                      </Box>
                    </Box>

                    <Box>
                      <ControlledExcuseCheckbox
                        name="excusedFromSchoolOn"
                        label={t(`telemed.plan.excuse.fields.excusedFromSchoolOn`)}
                      />
                      <ControlledExcuseDatePicker
                        name="excusedFromSchoolOnDate"
                        validate={(value) => {
                          if (getValues('excusedFromSchoolOn') && !value) {
                            return t('telemed.plan.excuse.fields.required');
                          }
                          return;
                        }}
                      />
                    </Box>

                    <ControlledExcuseCheckbox
                      name="excusedFromSchoolUntilFeverFreeFor24Hours"
                      label={t(`telemed.plan.excuse.fields.excusedFromSchoolUntilFeverFreeFor24Hours`)}
                    />
                    <ControlledExcuseCheckbox
                      name="excusedFromSchoolUntilOnAntibioticsFor24Hours"
                      label={t(`telemed.plan.excuse.fields.excusedFromSchoolUntilOnAntibioticsFor24Hours`)}
                    />
                    <ControlledExcuseCheckbox
                      name="ableToReturnToSchoolWithoutRestriction"
                      label={t(`telemed.plan.excuse.fields.ableToReturnToSchoolWithoutRestriction`)}
                    />
                    <ControlledExcuseCheckbox
                      name="ableToReturnToGymActivitiesWithoutRestriction"
                      label={t(`telemed.plan.excuse.fields.ableToReturnToGymActivitiesWithoutRestriction`)}
                    />
                    <ControlledExcuseCheckbox
                      name="ableToReturnToSchoolWhenThereNoLongerIsAnyEyeDischarge"
                      label={t(`telemed.plan.excuse.fields.ableToReturnToSchoolWhenThereNoLongerIsAnyEyeDischarge`)}
                    />

                    <Box sx={{ display: 'flex' }}>
                      <ControlledExcuseCheckbox
                        name="excusedFromGymActivitiesFromTo"
                        label={t(`telemed.plan.excuse.fields.excusedFromGymActivitiesFromTo`)}
                      />

                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <ControlledExcuseDatePicker
                          name="excusedFromGymActivitiesFromDate"
                          validate={(value) => {
                            if (getValues('excusedFromGymActivitiesFromTo') && !value) {
                              return t('telemed.plan.excuse.fields.required');
                            }
                            return;
                          }}
                        />
                        <ControlledExcuseDatePicker
                          name="excusedFromGymActivitiesToDate"
                          validate={(value) => {
                            if (getValues('excusedFromGymActivitiesFromTo') && !value) {
                              return t('telemed.plan.excuse.fields.required');
                            }
                            return;
                          }}
                        />
                      </Box>
                    </Box>

                    <ControlledExcuseCheckbox
                      name="ableToReturnToSchoolWithTheFollowingRestrictions"
                      label={t(`telemed.plan.excuse.fields.ableToReturnToSchoolWithTheFollowingRestrictions`)}
                    />

                    <Box sx={{ display: 'flex', flexDirection: 'column', ml: 5 }}>
                      <ControlledExcuseCheckbox
                        name="dominantHandIsInjuredPleaseAllowAccommodations"
                        label={t(`telemed.plan.excuse.fields.dominantHandIsInjuredPleaseAllowAccommodations`)}
                        onChange={(newValue) =>
                          newValue && setValue('ableToReturnToSchoolWithTheFollowingRestrictions', true)
                        }
                      />
                      <ControlledExcuseCheckbox
                        name="needsExtraTimeBetweenClassesAssistantOrBookBuddy"
                        label={t(`telemed.plan.excuse.fields.needsExtraTimeBetweenClassesAssistantOrBookBuddy`)}
                        onChange={(newValue) =>
                          newValue && setValue('ableToReturnToSchoolWithTheFollowingRestrictions', true)
                        }
                      />

                      <Box>
                        <ControlledExcuseCheckbox
                          name="otherRestrictions"
                          label={t(`telemed.plan.excuse.fields.otherRestrictions`)}
                          onChange={(newValue) =>
                            newValue && setValue('ableToReturnToSchoolWithTheFollowingRestrictions', true)
                          }
                        />

                        <ControlledExcuseTextField
                          name="otherRestrictionsNote"
                          placeholder={t('telemed.plan.excuse.fields.specify')}
                          validate={(value) => {
                            if (getValues('otherRestrictions') && !value) {
                              return t('telemed.plan.excuse.fields.required');
                            }
                            return;
                          }}
                        />
                      </Box>
                    </Box>

                    <ControlledExcuseCheckbox
                      name="allowedUseOfCrutchesAceWrapSplintAndElevatorAsNecessary"
                      label={t(`telemed.plan.excuse.fields.allowedUseOfCrutchesAceWrapSplintAndElevatorAsNecessary`)}
                    />
                    <ControlledExcuseCheckbox
                      name="allowedUseOfElevatorAsNecessary"
                      label={t(`telemed.plan.excuse.fields.allowedUseOfElevatorAsNecessary`)}
                    />
                    <ControlledExcuseCheckbox
                      name="unableToParticipateInGymActivitiesUntilClearedByAPhysician"
                      label={t(`telemed.plan.excuse.fields.unableToParticipateInGymActivitiesUntilClearedByAPhysician`)}
                    />

                    <Box sx={{ display: 'flex' }}>
                      <ControlledExcuseCheckbox
                        name="schoolExcusedFromWorkFromTo"
                        label={t(`telemed.plan.excuse.fields.schoolExcusedFromWorkFromTo`)}
                      />

                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <ControlledExcuseDatePicker
                          name="schoolExcusedFromWorkFromDate"
                          validate={(value) => {
                            if (getValues('schoolExcusedFromWorkFromTo') && !value) {
                              return t('telemed.plan.excuse.fields.required');
                            }
                            return;
                          }}
                        />
                        <ControlledExcuseDatePicker
                          name="schoolExcusedFromWorkToDate"
                          validate={(value) => {
                            if (getValues('schoolExcusedFromWorkFromTo') && !value) {
                              return t('telemed.plan.excuse.fields.required');
                            }
                            return;
                          }}
                        />
                      </Box>
                    </Box>

                    <Box>
                      <ControlledExcuseCheckbox
                        name="schoolExcusedFromWorkOn"
                        label={t(`telemed.plan.excuse.fields.schoolExcusedFromWorkOn`)}
                      />

                      <ControlledExcuseDatePicker
                        name="schoolExcusedFromWorkOnDate"
                        validate={(value) => {
                          if (getValues('schoolExcusedFromWorkOn') && !value) {
                            return t('telemed.plan.excuse.fields.required');
                          }
                          return;
                        }}
                      />
                    </Box>

                    <Box>
                      <ControlledExcuseCheckbox
                        name="ableToReturnToWorkWithTheFollowingRestrictions"
                        label={t(`telemed.plan.excuse.fields.ableToReturnToWorkWithTheFollowingRestrictions`)}
                      />

                      <ControlledExcuseTextField
                        name="ableToReturnToWorkWithTheFollowingRestrictionsNote"
                        placeholder={t('telemed.plan.excuse.fields.specify')}
                        validate={(value) => {
                          if (getValues('ableToReturnToWorkWithTheFollowingRestrictions') && !value) {
                            return t('telemed.plan.excuse.fields.required');
                          }
                          return;
                        }}
                      />
                    </Box>
                    <Box>
                      <ControlledExcuseCheckbox name="other" label={t(`telemed.plan.excuse.fields.other`)} />

                      <ControlledExcuseTextField
                        name="otherNote"
                        placeholder={t('telemed.plan.excuse.fields.specify')}
                        validate={(value) => {
                          if (getValues('other') && !value) {
                            return t('telemed.plan.excuse.fields.required');
                          }
                          return;
                        }}
                      />
                    </Box>
                  </>
                )}
              </FormGroup>
            </FormControl>
          )}

          {fields.includes('footerNote') && (
            <ControlledExcuseTextField
              name="footerNote"
              label={t('telemed.plan.excuse.fields.note')}
              fullWidth
              multiline
              required
            />
          )}
        </Box>
      </FormProvider>
    </GenerateExcuseDialogContainer>
  );
};
